import { useLocalStorage } from '@vueuse/core'

import { toast } from '@/common/util'
import { extractNotifications } from '@/common/apiError'
import baseApi from './base.api'
import BaseService from './base.service'
import { useAuthStore } from '@/stores'
import { aesCryptographyService } from './aesCryptography.service'

import type { PaginationParamsType, UsersBatchType, UsersBasicType } from '@/types/types'

export const usersService = {
  ...baseApi,
  resource: 'usuario',

  // @TODO: O Endpoint foi adicionado na service mas como os filtros não estão funcionando não sera usado no momento, 
  //mas ja fica implementado para uso futuro
  async getAll(opt?: {
    tenantId?: string
    usersIds?: string[]
    businessUnitIds?: string[]
    departmentIds?: string[]
    positionIds?: string[]
    positionId?: string
    partnerId?: string
    gender?: string
    genres?: string[]
    active?: boolean
    name?: string
    search?: string
    page?: number
    size?: number
  }, options?: any) {
    const paramsDictionary = {
      tenantId: 'tenantId',
      active: 'ativo',
      name: 'nomeCompleto',
      search: 'search',

      usersIds: 'usuarioIds',
      businessUnitIds: 'unidadeDeNegocioIds',
      departmentIds: 'departamentoIds',
      positionIds: 'CargoIds',
      positionId: 'cargoId',
      partnerId: 'partnerId',
      gender: 'genero',
      genres: 'generos',
      page: 'page',
      size: 'size',
    }

    const paramsToSend = {}

    if (opt) {
      Object.keys(opt).forEach((key) => {
        if (typeof paramsDictionary[key] !== 'undefined' && typeof opt[key] !== 'undefined' && opt[key] !== null)
          paramsToSend[paramsDictionary[key]] = opt[key]
      })
    }

    return usersService.get<any>('', paramsToSend, options)
      .then((res) => ({
        status: res.success,
        page: res.pageNumber,
        size: res.pageSize,
        total: res.totalItems,
        pages: res.totalPages,
        data: res.data?.map((item) => ({
          id: item.id || null,
          name: item.nomeCompleto,
          active: item.active,
          email: item.email,
          cpf: item.cpf,
          phone: item.telefone,
          personType: item.tipoPessoa,
          hireDate: item.dataDeAdmissao,
          directSupervisor: item.superiorDireto,
          directSupervisorName: item.superiorDiretoNome,
          positionId: item.cargoId,
          positionName: item.cargoNome,
          departmentId: item.departamentoId,
          departmentName: item.departamentoNome,
          businessUnitId: item.unidadeDeNegocioId,
          businessUnitName: item.unidadeDeNegocioNome,
          permissionId: item.permissaoId,
          gender: item.genero,
          nickname: item.apelido,
          preferredLanguage: item.idiomaPreferido,
          birthDate: item.dataDeNascimento,
          notifyViaPush: item.notificarViaPush,
          notifyViaSMS: item.notificarViaSMS,
          ativo: item.ativo,
          peopleId: item.peopleId,
          photo: item.foto,
          createdAt: item.createdAt,
          tenantId: item.tenantId,
          educationLevel: item.escolaridade,
          skillsId: item.competenciasId,
          termsOfUseAcceptedDate: item.dataTermoDeUsoAceito,
          termsOfUseId: item.termoDeUsoId,
          isForeign: item.estrangeira,
          achievements: item.conquistas,
        }))
      }))
  },

  async getListBasic(params?: UsersBasicType): Promise<{
    success: boolean, data: {
      id: string,
      nomeCompleto: string
      email: string
      active?: boolean
      isAvaliador?: boolean
    }[]
  }> {
    try {
      return await usersService.get(`Basic`, { ...params })
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao buscar lista de usuários!' })
    }
  },

  async changeIndividualPassword(
    data: {
      usuarioId: string
      alterarSenhaNoProximoAcesso: boolean
      senha: string
    }) {

    const encryptedPassword = await aesCryptographyService.encrypt(data.senha)
    // @ts-ignore
    data.senha = encryptedPassword.data

    try {
      const { success, data: result, notifications } = await usersService.post(`alterar-senha-individual`, data, { silent: true }) as any
      return { success, data: result, notifications: notifications || [] }
    } catch (error) {
      return { success: false, data: null, notifications: extractNotifications(error) }
    }
  },

  async changePasswordInBatch(
    data: {
      usuarioIds: string[]
      alterarSenhaNoProximoAcesso: boolean
      senha: string
    }) {

    const encryptedPassword = await aesCryptographyService.encrypt(data.senha)
    // @ts-ignore
    data.senha = encryptedPassword.data

    try {
      const { success, data: result, notifications } = await usersService.post(`alterar-senha-em-lote`, data, { silent: true }) as any
      return { success, data: result, notifications: notifications || [] }
    } catch (error) {
      return { success: false, data: null, notifications: extractNotifications(error) }
    }
  },

  uploadUserPicture(data: { peopleId: string, pictureBase64: string }) {
    try {
      return usersService.put(`Picture`, {
        peopleId: data.peopleId,
        pictureBase64: data.pictureBase64
      })
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao buscar lista de usuários!' })
    }
  }
}

class UsuariosService extends BaseService {
  constructor() {
    super({ resource: 'usuario' })
  }

  async recoverPassword(email: string) {
    return await this.plantarApi.get(`${this.resource}/forgotpassword/${email}`)
  }

  async getProgressUser() {
    return await this.plantarApi.get(`${this.resource}/progresso`)
  }

  async getUsersByTenant(tenantId: string, partnerId?: string, pagination?: PaginationParamsType) {
    try {
      return await this.plantarApi.get(`${this.resource}`, {
        params: {
          tenantId,
          partnerId: partnerId,
          page: pagination?.page || 1
          // size: pagination?.pageSize || 10
        }
      })
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] })
    }
  }

  async getSubscriptionsUser(userId: string) {
    return await this.plantarApi.get(`${this.resource}/inscricao/${userId}`)
  }

  async acceptTermOfUse(userId: string) {
    try {
      return await this.plantarApi.put(`${this.resource}/termoDeUso/${userId}`)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar aceitar o termo de uso' })
    }
  }

  async validateFileImport(file: File): Promise<{ success: boolean; data: UsersBatchType }> {
    const formData = new FormData()
    formData.append('usersBatch', file)
    return await this.plantarApi.post(`${this.resource}/validate/batch`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async importUsers(fileId: string) {
    try {
      return await this.plantarApi.post(`${this.resource}/import/batch/${fileId}`)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar importar dados!' })
    }
  }

  async getImportStatus(batchId: string) {
    try {
      const response = await this.plantarApi.get(`${this.resource}/ImportStatus/${batchId}`)
      return response.data || response
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao consultar status da importação!' })
      return null
    }
  }

  async getUserInfoNPS() {
    try {
      return await this.plantarApi.get(`${this.resource}/nps`)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] })
    }
  }

  async updateUserEmail(id: string, email: string) {
    try {
      return await this.plantarApi.put(`${this.resource}/updateEmail`, { id, newEmail: email, resetPassword: true })
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar atualizar e-mail do usuário!' })
    }
  }

  async getUserAccounts() {
    try {
      const { success, data } = await this.plantarApi.get(`${this.resource}/useraccount`)

      const persistentInfo = useLocalStorage<any>('userInfo', { multiplePartners: false }, { mergeDefaults: true })
      persistentInfo.value.multiplePartners = success && data.length > 1

      return { data }
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao recuperar dados de contas do usuário!' })
    }
  }

  async setDefaultPartner(tenantId: string) {
    try {
      return await this.plantarApi.post(`${this.resource}/useraccount/tenant/${tenantId}`)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar definir conta como padrão!' })
    }
  }

  async getPasswordCode(userId: string) {
    const data = new TextEncoder().encode(`${userId}_plataforma-aliare_secret`)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hash = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    try {
      const { data, success } = await this.plantarApi.get<{ link: string }>(`${this.resource}/${userId}/LinkResetPassword?hash=${hash}`)

      return {
        token: ((new URLSearchParams(data?.link || '')).get('token') || '').split(' ').join('+'),
        success
      }
    } catch (error) {
      return { success: false, token: '' }
    }
  }

  async changePassword(opts: {
    userId: string
    email: string
    password: string
    clientId?: string
    tenantId?: string
  }) {
    const { token, success: hasToken } = await this.getPasswordCode(opts.userId)

    if (!hasToken) {
      toast.error({ message: 'Erro ao tentar recuperar token de alteração de senha!' })
    }

    try {
      const { data, success } = await this.plantarApi.post<{ link: string }>(`${this.resource}/CreatePassword`, {
        userId: opts.userId,
        email: opts.email,
        password: opts.password,
        token,
        clientId: opts?.clientId || (window as any)?.configuration?.aliare?.credentials?.client_id || '',
        tenantId: opts?.tenantId || useAuthStore().getActiveTentant
      })

      return {
        success,
        data
      }
    } catch (error) {
      return {
        success: false,
        data: null
      }
    }
  }
}

const usuariosService = new UsuariosService()

export default usuariosService
